import { createContext, useContext, useMemo, useReducer } from "react"
import { usePreferences } from "hooks/use-preferences"

export const FactorsViewContext = createContext()

const PREFERENCE_SUBTYPE_BASE = "factors-view"

function getPreferenceSubType(panelId) {
  return panelId ? `${PREFERENCE_SUBTYPE_BASE}.${panelId}` : PREFERENCE_SUBTYPE_BASE
}

const DEFAULT_STATE = {
  view: "risk",
  groupBy: null,
  showMetricsSelector: false,
  selectedPeriodPerformance: "1M",
  // ContributorsTable sorting state
  sortMetricId: "riskDecomposition",
  sortDirection: "DESC",
  // ContributorsTable navigation state
  selectedTableLevels: [],
  // DataGrid state persistence
  datagridState: {},
}

// Define which fields should be saved as preferences
const PREFERENCE_FIELDS = [
  "view",
  "groupBy",
  "showNotHeld",
  "selectedPeriodPerformance",
  "sortMetricId",
  "sortDirection",
  "selectedTableLevels",
  "datagridState",
]

function reducer(current, action) {
  switch (action.type) {
    case "setView": {
      const next = { ...current, view: action.view }
      return next
    }
    case "setGroupBy":
      return {
        ...current,
        groupBy: action.groupBy,
      }
    case "setShowNotHeld":
      return {
        ...current,
        showNotHeld: action.showNotHeld,
      }
    case "setShowMetricsSelector":
      return {
        ...current,
        showMetricsSelector: action.showMetricsSelector,
      }
    case "setSelectedPeriodPerformance":
      return {
        ...current,
        selectedPeriodPerformance: action.selectedPeriodPerformance,
      }
    case "setSortMetricId":
      return {
        ...current,
        sortMetricId: action.sortMetricId,
      }
    case "setSortDirection":
      return {
        ...current,
        sortDirection: action.sortDirection,
      }
    case "setSelectedTableLevels":
      return {
        ...current,
        selectedTableLevels: action.selectedTableLevels,
      }
    case "setDatagridState":
      return {
        ...current,
        datagridState: action.datagridState,
      }
    case "setPreferences": {
      const next = { ...current, ...(action.preferences || {}) }
      return next
    }
    default:
      return current
  }
}

function setView(view) {
  return {
    type: "setView",
    view,
  }
}

function setGroupBy(groupBy) {
  return {
    type: "setGroupBy",
    groupBy,
  }
}

function setShowNotHeld(showNotHeld) {
  return {
    type: "setShowNotHeld",
    showNotHeld,
  }
}

function setShowMetricsSelector(showMetricsSelector) {
  return {
    type: "setShowMetricsSelector",
    showMetricsSelector,
  }
}

function setSortMetricId(sortMetricId) {
  return {
    type: "setSortMetricId",
    sortMetricId,
  }
}

function setSortDirection(sortDirection) {
  return {
    type: "setSortDirection",
    sortDirection,
  }
}

function setSelectedTableLevels(selectedTableLevels) {
  return {
    type: "setSelectedTableLevels",
    selectedTableLevels,
  }
}

function setDatagridState(datagridState) {
  return {
    type: "setDatagridState",
    datagridState,
  }
}

function setPreferences(preferences) {
  return {
    type: "setPreferences",
    preferences,
  }
}

/** Dashboard `module.config` key → factors state key. Seeded values are omitted from generic fallback prefs. */
const MODULE_CONFIG_TO_STATE = {
  initialView: "view",
  sortMetricId: "sortMetricId",
  selectedTableLevels: "selectedTableLevels",
}

function seedStateFromModuleConfig(moduleConfig) {
  const patch = {}
  const omitFallbackKeys = []
  if (moduleConfig == null || typeof moduleConfig !== "object") {
    return { initialState: { ...DEFAULT_STATE }, omitFallbackKeys }
  }
  for (const [configKey, stateKey] of Object.entries(MODULE_CONFIG_TO_STATE)) {
    const value = moduleConfig[configKey]
    if (value == null) continue
    patch[stateKey] = Array.isArray(value) ? [...value] : value
    omitFallbackKeys.push(stateKey)
  }
  return {
    initialState: { ...DEFAULT_STATE, ...patch },
    omitFallbackKeys,
  }
}

export default function useFactorsViewContext(parentContext, options = {}) {
  const { moduleConfig, panelId } = options
  const parentContextValue = useContext(parentContext)
  const preferenceSubType = useMemo(() => getPreferenceSubType(panelId), [panelId])

  const { initialState, preferenceOptions } = useMemo(() => {
    const { initialState: nextInitial, omitFallbackKeys } = seedStateFromModuleConfig(moduleConfig)
    return {
      initialState: nextInitial,
      preferenceOptions: {
        fallbackSubType: panelId ? PREFERENCE_SUBTYPE_BASE : undefined,
        ...(omitFallbackKeys.length > 0 && { omitFallbackKeys }),
      },
    }
  }, [moduleConfig, panelId])

  const [state, dispatch] = useReducer(reducer, initialState)

  const isPreferencesLoaded = usePreferences(
    preferenceSubType,
    PREFERENCE_FIELDS,
    state,
    dispatch,
    setPreferences,
    preferenceOptions,
  )

  const contextUpdaters = useMemo(() => ({
    setView: (...args) => dispatch(setView(...args)),
    setGroupBy: (...args) => dispatch(setGroupBy(...args)),
    setShowNotHeld: (...args) => dispatch(setShowNotHeld(...args)),
    setShowMetricsSelector: (...args) => dispatch(setShowMetricsSelector(...args)),
    setSortMetricId: (...args) => dispatch(setSortMetricId(...args)),
    setSortDirection: (...args) => dispatch(setSortDirection(...args)),
    setSelectedTableLevels: (...args) => dispatch(setSelectedTableLevels(...args)),
    setDatagridState: (...args) => dispatch(setDatagridState(...args)),
  }), [dispatch])

  const contextValue = useMemo(() => ({
    ...parentContextValue,
    ...contextUpdaters,
    ...state,
    isPreferencesLoaded,
  }), [state, parentContextValue, contextUpdaters, isPreferencesLoaded])

  return [FactorsViewContext, contextValue]
}
